'use client';
import React, { useEffect, useRef, useState } from 'react';
import { Carousel } from 'react-responsive-carousel';
import formatRepliersImageSource from '@th/shared/src/helpers/formatRepliersImageSource';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { SimpleListing } from '@th/shared/src/types/SimpleListing';
import { twMerge } from 'tailwind-merge';
import UnauthenticatedCarouselOverlay from './UnauthenticatedCarouselOverlay';
import ListingInfoBadge from './ListingInfoBadge';
import SvgIcon from '../icons/SvgIcon';
import Link from 'next/link';
import 'react-loading-skeleton/dist/skeleton.css';
import skeletonStyles from '../../styles/loadingSkeleton.module.scss';
import useLazyLoadImageCarousel from '../../hooks/useLazyLoadImageCarousel';

type Props = {
  listing: SimpleListing;
  className?: string;
  imageSize: 'small' | 'medium' | 'large';
  isBlurred?: boolean;
  small?: boolean;
} & OnClickProps;

type OnClickProps =
  | { onClickCallback: () => void; onClickLink?: never }
  | {
      onClickCallback?: never;
      onClickLink: string;
    };

export default function ListingCarousel({
  listing,
  className,
  imageSize,
  onClickCallback,
  onClickLink,
  isBlurred,
  small,
}: Props) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const images = listing.images.map(image => {
    return formatRepliersImageSource(image, imageSize);
  });

  // const [isFirstImageLoaded, setIsFirstImageLoaded] = useState(false);
  // const imagesToLoad = useLazyLoadImageCarousel(
  //   images,
  //   currentIndex,
  //   isFirstImageLoaded,
  // );

  if (isBlurred) {
    return (
      <div className={twMerge('relative', className)}>
        <div className="aspect-3/2 relative" id={'container'}>
          <img
            id={'unauthed-img'}
            src={images[0]?.uri || '/images/imagePlaceholder.png'}
            alt={`Blurred preview image for ${listing.address.formattedAddressShortOneLiner()}`}
            className="opacity-0 object-cover align-bottom aspect-3/2 blur-sm absolute inset-0"
            key={images[0]?.uri || 'no-image'}
            onLoad={() => {
              document.getElementById('skeleton')?.remove();
              document.getElementById('unauthed-img').style.opacity = '1';
            }}
          />
          <div
            id="skeleton"
            className={twMerge(
              'aspect-3/2',
              skeletonStyles['loading-skeleton'],
            )}
          />
        </div>
        <UnauthenticatedCarouselOverlay small={small} />
      </div>
    );
  }

  return (
    <div className={twMerge('relative', className)}>
      <Carousel
        axis={'horizontal'}
        showArrows
        showStatus={false}
        showIndicators={false}
        infiniteLoop
        showThumbs={false}
        useKeyboardArrows
        swipeable
        dynamicHeight={false}
        emulateTouch
        autoFocus={false}
        thumbWidth={100}
        selectedItem={0}
        transitionTime={500}
        swipeScrollTolerance={5}
        onChange={setCurrentIndex}
        renderArrowNext={(onClickHandler, hasNext, label) =>
          hasNext && (
            <CarouselNextPrevButton
              onClick={onClickHandler}
              direction={'right'}
            />
          )
        }
        renderArrowPrev={(onClickHandler, hasPrev, label) =>
          hasPrev && (
            <CarouselNextPrevButton
              onClick={onClickHandler}
              direction={'left'}
            />
          )
        }
      >
        {images.map((image, index) => {
          if (onClickLink) {
            return (
              <Link
                key={image.uri}
                href={onClickLink}
                className="group block"
                target={'_blank'}
              >
                <img
                  src={image.uri}
                  alt={`Preview image for ${listing.address.formattedAddressShortOneLiner()}`}
                  className="object-cover align-bottom aspect-3/2 transition-all group-hover:scale-110"
                  onLoad={() => {
                    document.getElementById(`skeleton-${index}`)?.remove();
                    // if (index === 0) {
                    //   setIsFirstImageLoaded(true);
                    // }
                  }}
                />
                <div
                  id={`skeleton-${index}`}
                  className={twMerge(
                    'aspect-3/2',
                    skeletonStyles['loading-skeleton'],
                  )}
                />
              </Link>
            );
          } else {
            return (
              <img
                key={image.uri}
                src={image.uri}
                alt={`Preview image for ${listing.address.formattedAddressShortOneLiner()}`}
                className="object-cover align-bottom aspect-3/2"
              />
            );
          }
        })}
      </Carousel>
      {onClickCallback && (
        <div
          className="absolute bg-transparent top-0 bottom-0 left-16 right-16 cursor-pointer"
          onClick={onClickCallback}
        />
      )}
      <div className="absolute bottom-3 right-3 bg-semiTransparentDarkGray px-3 py-2 rounded-xl">
        <p className="p-0 m-0 text-gray-300">
          {currentIndex + 1} / {images.length}
        </p>
      </div>
      <ListingInfoBadge listing={listing} />
    </div>
  );
}

function CarouselNextPrevButton({
  onClick,
  direction,
}: {
  onClick: () => void;
  direction: 'left' | 'right';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="absolute top-[calc(50%-1.2rem)] z-10 bg-white opacity-80 hover:opacity-100 rounded-full flex items-center justify-center w-[2.4rem] h-[2.4rem]"
      style={{
        position: 'absolute',
        right: direction === 'right' ? 15 : undefined,
        left: direction === 'left' ? 15 : undefined,
      }}
    >
      <SvgIcon
        name={direction === 'right' ? 'HiChevronRight' : 'HiChevronLeft'}
        className={'text-text w-[1.5rem] h-[1.5rem] '}
      />
    </button>
  );
}
